import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  signInWithEmailAndPassword,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence, // added
} from "firebase/auth";
import { auth } from "../firebase";
import { BASE_URL } from "../apiConfig";

import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";

import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CheckCircle from "@mui/icons-material/CheckCircle";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import RadioButtonUnchecked from "@mui/icons-material/RadioButtonUnchecked";

type StepState = "idle" | "active" | "done" | "error";

export default function SignIn() {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [showPassword, setShowPassword] = useState(false);

  const [loading, setLoading] = useState(false);

  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const [showError, setShowError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const [steps, setSteps] = useState<{ label: string; state: StepState }[]>([
    { label: "Authenticating", state: "idle" },
    { label: "Verifying access", state: "idle" },
  ]);

  const currentStepIndex = useMemo(
    () => steps.findIndex((s) => s.state === "active"),
    [steps]
  );

  const setStepState = (index: number, state: StepState) => {
    setSteps((prev) => prev.map((s, i) => (i === index ? { ...s, state } : s)));
  };

  const startSteps = () => {
    setSteps([
      { label: "Authenticating", state: "active" },
      { label: "Verifying access", state: "idle" },
    ]);
  };

  const resetSteps = () => {
    setSteps([
      { label: "Authenticating", state: "idle" },
      { label: "Verifying access", state: "idle" },
    ]);
  };

  const validate = () => {
    if (!email.trim() || !password.trim()) {
      setErrorMsg("Email and password are required.");
      setShowError(true);
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErrorMsg("Please enter a valid email address.");
      setShowError(true);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setSuccessMsg("");
    setShowSuccess(false);
    setErrorMsg("");
    setShowError(false);
    startSteps();

    try {
      await setPersistence(auth, remember ? browserLocalPersistence : browserSessionPersistence);

      const cred = await signInWithEmailAndPassword(auth, email, password);
      const token = await cred.user.getIdToken();

      localStorage.setItem("authToken", token);
      localStorage.setItem("uid", cred.user.uid);

      setStepState(0, "done");
      setStepState(1, "active");

      const resp = await fetch(`${BASE_URL}/user/verify`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`, // FIX: add Bearer
        },
      });

      const data = await resp.json().catch(() => ({}));
      if (resp.ok && data?.status === "success") {
        console.log("User data:", data);
        localStorage.setItem("userRole", data.role || "user");
        localStorage.setItem("userName", data.name || "");
        localStorage.setItem("userId", data.user_id || "");
        window.dispatchEvent(new Event("userChanged"));
        setStepState(1, "done");
        setSuccessMsg("Logged in successfully!");
        setShowSuccess(true);
        setTimeout(() => navigate("/"), 1000);
      } else {
        const m = data?.message || "Verification failed.";
        setStepState(1, "error");
        throw new Error(m);
      }
    } catch (err: any) {
      let msg = "Login failed. Please try again.";
      if (err?.code === "auth/invalid-credential" || err?.code === "auth/user-not-found") {
        msg = "Invalid email or password.";
      } else if (err?.code === "auth/wrong-password") {
        msg = "Incorrect password.";
      } else if (err instanceof Error && err.message) {
        msg = err.message;
      }
      const idx = currentStepIndex >= 0 ? currentStepIndex : 0;
      setStepState(idx, "error");
      setErrorMsg(msg);
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const prev = document.body.style.overflowY;
    document.body.style.overflowY = "hidden";
    return () => {
      document.body.style.overflowY = prev;
    };
  }, []);

  return (
    <div
      className="bg-white flex px-4 md:px-6 overflow-hidden"
      style={{ height: "100dvh" }} // dvh avoids the 100vh off-by-1 scroll
    >
      {/* Content */}
      <div className="relative z-10 flex flex-col w-full">
        {/* Top space removed */}
        {/* <div className="h-0 md:h-0" /> */}
        <div className="flex flex-col md:flex-row justify-between items-center px-0 md:px-6 h-full">
          {/* Left: Quote based on Hero section */}
          <div className="hidden md:flex w-full md:flex-1 justify-center md:justify-start mb-8 md:mb-0">
            <div className="w-full max-w-md border border-slate-200 rounded-xl p-6 bg-slate-50">
              <div className="text-slate-900 text-xl font-semibold font-[Inter]">
                Agentic AI for Data–to–Deployment
              </div>
              <p className="mt-3 text-slate-700 italic">
                “Turn raw, siloed data into actionable insights you can deploy and share—autonomously and efficiently.”
              </p>
              <p className="mt-2 text-slate-500 text-sm">
                Built for teams that value autonomy, speed, and shareability.
              </p>
            </div>
          </div>

          {/* Right: form card */}
          <div className="w-full md:w-[440px] flex flex-col items-center justify-center md:ml-16 mt-4 md:mt-0">
            <div className="w-full max-w-[380px] bg-black border border-slate-800 rounded-xl p-5 md:p-6 shadow-lg">
              <div className="flex flex-col items-center gap-1 mb-4">
                <h3 className="font-inter font-semibold text-[20px] md:text-[22px] leading-[30px] text-slate-100 text-center">
                  Sign in
                </h3>
                <p className="font-inter text-[14px] leading-[20px] text-slate-400 text-center">
                  Use your email and password
                </p>
              </div>

              <form onSubmit={handleSubmit} className="w-full flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <label className="block text-[13px] md:text-sm text-slate-200" htmlFor="email">
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    disabled={loading}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full h-[42px] px-3 bg-black border border-slate-700 rounded-[12px] text-slate-100 text-[14px] placeholder-slate-500 focus:outline-none"
                    placeholder="example@company.com"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label className="block text-[13px] md:text-sm text-slate-200" htmlFor="password">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      disabled={loading}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full h-[42px] px-3 pr-10 bg-black border border-slate-700 rounded-[12px] text-slate-100 text-[14px] placeholder-slate-500 focus:outline-none"
                      placeholder="••••••••••"
                    />
                    <IconButton
                      size="small"
                      onClick={() => setShowPassword((s) => !s)}
                      className="!absolute !right-2 !top-1/2 -translate-y-1/2"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <VisibilityOff sx={{ color: "#A1A1AA" }} fontSize="small" />
                      ) : (
                        <Visibility sx={{ color: "#A1A1AA" }} fontSize="small" />
                      )}
                    </IconButton>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 select-none">
                    <input
                      type="checkbox"
                      className="w-4 h-4 rounded border-slate-600"
                      checked={remember}
                      onChange={(e) => setRemember(e.target.checked)}
                      disabled={loading}
                    />
                    <span className="text-[12px] md:text-sm text-slate-300">Remember me</span>
                  </label>
                  {/* Forgot password removed */}
                </div>

                {/* Status steps (dark) */}
                {(loading || steps.some((s) => s.state === "done" || s.state === "error")) && (
                  <div className="mt-1 rounded-lg border border-slate-800 bg-black p-3 space-y-2">
                    {steps.map((s, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm">
                        {s.state === "active" && <CircularProgress size={16} sx={{ color: "#94a3b8" }} />}
                        {s.state === "done" && <CheckCircle fontSize="small" sx={{ color: "#22c55e" }} />}
                        {s.state === "error" && <ErrorOutline fontSize="small" sx={{ color: "#f43f5e" }} />}
                        {s.state === "idle" && <RadioButtonUnchecked fontSize="small" sx={{ color: "#334155" }} />}
                        <span
                          className={
                            s.state === "error"
                              ? "text-rose-400"
                              : s.state === "done"
                              ? "text-emerald-400"
                              : "text-slate-300"
                          }
                        >
                          {s.label}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full h-[42px] mt-1 rounded-[8px] font-inter font-medium text-[14px]
                             bg-white text-slate-900 hover:bg-slate-100 border border-slate-300
                             disabled:opacity-60 transition-colors"
                >
                  {loading ? "Signing in..." : "Sign in"}
                </button>

                <div className="text-center text-xs text-slate-400">
                  By continuing you agree to our Terms and Privacy Policy.
                </div>

                {/* Reset steps when user edits after an attempt */}
                <input
                  type="hidden"
                  value={email + password}
                  onChange={() => {}}
                  onInput={resetSteps}
                />
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Toasts */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={2500}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success" variant="filled" onClose={() => setShowSuccess(false)}>
          {successMsg || "Logged in successfully!"}
        </Alert>
      </Snackbar>

      <Snackbar
        open={showError}
        autoHideDuration={3000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="error" variant="filled" onClose={() => setShowError(false)}>
          {errorMsg || "Login failed. Please try again."}
        </Alert>
      </Snackbar>
    </div>
  );
}

